import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, LogOut, Loader2, Star, Trash2, Plus, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hgar_admin_token";
const STATUSES = ["new", "contacted", "scheduled", "completed"];

const STATUS_STYLE = {
  new: "text-royal",
  contacted: "text-amber-400",
  scheduled: "text-sky-400",
  completed: "text-emerald-400",
};

const inputCls =
  "w-full border border-line bg-void px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-400 outline-none transition-colors focus:border-gold";

const labelCls =
  "mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-300";

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchMe(token) {
  const { data } = await axios.get(`${API}/auth/me`, {
    headers: authHeaders(token),
    withCredentials: true,
  });
  return data;
}

async function fetchBookings(token) {
  const { data } = await axios.get(`${API}/bookings`, {
    headers: authHeaders(token),
    withCredentials: true,
  });
  return data;
}

async function fetchAllReviews(token) {
  const { data } = await axios.get(`${API}/admin/reviews`, {
    headers: authHeaders(token),
    withCredentials: true,
  });
  return data;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        toast.success(`Signed in as ${data.user.email}`);
        navigate("/admin", { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed");
        navigate("/admin", { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="grid min-h-screen place-items-center bg-void">
      <Loader2 className="animate-spin text-gold" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
      <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
      <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-3 3.6-5 6.8-5z" />
    </svg>
  );
}

export default function Admin() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <AdminInner />;
}

function Stars({ rating, className = "" }) {
  return (
    <span className={`flex gap-0.5 ${className}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={13}
          className={n <= rating ? "fill-gold text-gold" : "fill-zinc-800 text-zinc-800"}
        />
      ))}
    </span>
  );
}

function AdminInner() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("amos.olayemi@yahoo.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [rvName, setRvName] = useState("");
  const [rvVehicle, setRvVehicle] = useState("");
  const [rvRating, setRvRating] = useState(5);
  const [rvQuote, setRvQuote] = useState("");
  const [addingReview, setAddingReview] = useState(false);

  const loadReviews = async (tok) => {
    setReviews(await fetchAllReviews(tok));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchMe(token);
        if (cancelled) return;
        setUser(me);
        setBookings(await fetchBookings(token));
        await loadReviews(token);
        if (cancelled) return;
        setChecking(false);
      } catch {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      setUser(await fetchMe(data.access_token));
      setBookings(await fetchBookings(data.access_token));
      await loadReviews(data.access_token);
      setChecking(false);
    } catch (err) {
      setChecking(false);
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign in failed. Try again.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // clearing local state regardless
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setBookings([]);
    setReviews([]);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/bookings/${id}`,
        { status },
        { headers: authHeaders(token), withCredentials: true }
      );
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const addReview = async (e) => {
    e.preventDefault();
    if (addingReview) return;
    setAddingReview(true);
    try {
      const { data } = await axios.post(
        `${API}/reviews`,
        { customer_name: rvName, vehicle: rvVehicle, rating: Number(rvRating), quote: rvQuote },
        { headers: authHeaders(token), withCredentials: true }
      );
      setReviews((rs) => [data, ...rs]);
      setRvName("");
      setRvVehicle("");
      setRvRating(5);
      setRvQuote("");
      toast.success("Review published to the site");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add review");
    } finally {
      setAddingReview(false);
    }
  };

  const toggleReview = async (r) => {
    try {
      const { data } = await axios.patch(
        `${API}/reviews/${r.id}`,
        { approved: !r.approved },
        { headers: authHeaders(token), withCredentials: true }
      );
      setReviews((rs) => rs.map((x) => (x.id === r.id ? data : x)));
      toast.success(data.approved ? "Review is live on the site" : "Review hidden from the site");
    } catch {
      toast.error("Could not update review");
    }
  };

  const deleteReview = async (r) => {
    try {
      await axios.delete(`${API}/reviews/${r.id}`, {
        headers: authHeaders(token),
        withCredentials: true,
      });
      setReviews((rs) => rs.filter((x) => x.id !== r.id));
      toast.success("Review deleted");
    } catch {
      toast.error("Could not delete review");
    }
  };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-void">
        <Loader2 className="animate-spin text-gold" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="grid min-h-screen place-items-center bg-void px-5">
        <div className="w-full max-w-sm border border-line bg-panel p-8" data-testid="admin-login-card">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
            <KeyRound size={13} /> Admin Access
          </p>
          <h1 className="mt-4 font-display text-3xl uppercase tracking-wide">
            His Grace Auto Repair
          </h1>

          <button
            onClick={googleSignIn}
            data-testid="admin-google-login-button"
            className="mt-8 flex w-full items-center justify-center gap-3 border border-zinc-600 bg-white px-6 py-3.5 font-cond font-bold uppercase tracking-[0.14em] text-zinc-900 transition-all duration-300 hover:border-gold hover:shadow-[0_0_24px_rgba(255,85,0,0.25)] active:scale-95"
          >
            <GoogleIcon />
            Sign in with Google
          </button>

          <div className="mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-line" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400">
              or email &amp; password
            </span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <form onSubmit={login} className="mt-6 space-y-5">
            <div>
              <label className="mb-2 block font-mono text-xs uppercase tracking-[0.22em] text-zinc-300" htmlFor="admin-email">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-login-email"
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="mb-2 block font-mono text-xs uppercase tracking-[0.22em] text-zinc-300" htmlFor="admin-password">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-login-password"
                type="password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="font-mono text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full bg-gold px-6 py-3.5 font-cond font-bold uppercase tracking-[0.18em] text-black transition-all hover:bg-gold-hover active:scale-95"
            >
              Sign In
            </button>
            <a href="/" className="block text-center font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-zinc-300">
              ← Back to site
            </a>
          </form>
        </div>
      </div>
    );
  }

  const visible = bookings.filter((b) => filter === "all" || b.status === filter);

  return (
    <div className="min-h-screen bg-void text-zinc-100">
      <header className="border-b border-line bg-panel/70 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              Admin{user?.email ? ` — ${user.email}` : ""}
            </p>
            <h1 className="mt-1 font-display text-2xl uppercase tracking-wide">
              {tab === "bookings" ? "Service Requests" : "Customer Reviews"}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {tab === "bookings" && (
              <select
                data-testid="admin-filter-status"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-line bg-void px-3 py-2 text-xs uppercase tracking-wider text-zinc-300 outline-none focus:border-gold"
              >
                <option value="all">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => setTab("bookings")}
              data-testid="admin-tab-bookings"
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${tab === "bookings" ? "border-gold bg-gold/10 text-gold" : "border-line text-zinc-400 hover:border-gold hover:text-gold"}`}
            >
              Bookings
            </button>
            <button
              onClick={() => setTab("reviews")}
              data-testid="admin-tab-reviews"
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${tab === "reviews" ? "border-gold bg-gold/10 text-gold" : "border-line text-zinc-400 hover:border-gold hover:text-gold"}`}
            >
              Reviews
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-gold hover:text-gold"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        {tab === "bookings" && (
          <>
            {visible.length === 0 ? (
              <p data-testid="admin-empty-state" className="border border-line bg-panel px-6 py-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">
                No bookings {filter === "all" ? "yet" : `with status "${filter}"`}
              </p>
            ) : (
              <div className="overflow-x-auto border border-line bg-panel" data-testid="admin-bookings-table">
                <table className="w-full min-w-[980px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-line font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">
                      {["Received", "Name", "Contact", "Vehicle", "Service", "Preferred", "Notes", "Status"].map((h) => (
                        <th key={h} className="px-4 py-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((b) => (
                      <tr key={b.id} className="border-b border-line/60 align-top last:border-0 hover:bg-elevated/50">
                        <td className="whitespace-nowrap px-4 py-4 font-mono text-xs text-zinc-400">
                          {new Date(b.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-4 font-medium text-zinc-100">{b.name}</td>
                        <td className="px-4 py-4 text-zinc-300">{b.contact}</td>
                        <td className="px-4 py-4 text-zinc-300">{b.vehicle}</td>
                        <td className="px-4 py-4 text-zinc-300">{b.service}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">{b.preferred_date || "—"}</td>
                        <td className="max-w-[220px] px-4 py-4 text-xs text-zinc-300">{b.notes || "—"}</td>
                        <td className="px-4 py-4">
                          <select
                            data-testid={`admin-booking-status-${b.id}`}
                            value={b.status}
                            onChange={(e) => setStatus(b.id, e.target.value)}
                            className={`border border-line bg-void px-2 py-1.5 font-mono text-[11px] uppercase tracking-wider outline-none focus:border-gold ${STATUS_STYLE[b.status] || "text-zinc-300"}`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400">
              {visible.length} of {bookings.length} requests shown
            </p>
          </>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-reviews-panel" className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <form
              onSubmit={addReview}
              data-testid="admin-review-form"
              className="h-fit space-y-4 border border-line bg-panel p-6 lg:col-span-5"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                Add a review
              </p>
              <p className="text-sm text-zinc-400">
                When a happy customer gives you feedback, add it here and it
                appears on the site right away.
              </p>
              <div>
                <label className={labelCls} htmlFor="rv-name">Customer name</label>
                <input
                  id="rv-name"
                  data-testid="admin-review-input-name"
                  className={inputCls}
                  value={rvName}
                  onChange={(e) => setRvName(e.target.value)}
                  placeholder="e.g. Maria G."
                  required
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="rv-vehicle">Vehicle (optional)</label>
                <input
                  id="rv-vehicle"
                  data-testid="admin-review-input-vehicle"
                  className={inputCls}
                  value={rvVehicle}
                  onChange={(e) => setRvVehicle(e.target.value)}
                  placeholder="e.g. 2016 Honda Civic"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="rv-rating">Rating</label>
                <select
                  id="rv-rating"
                  data-testid="admin-review-input-rating"
                  className={inputCls}
                  value={rvRating}
                  onChange={(e) => setRvRating(e.target.value)}
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="rv-quote">Their words</label>
                <textarea
                  id="rv-quote"
                  data-testid="admin-review-input-quote"
                  rows={4}
                  className={`${inputCls} resize-none`}
                  value={rvQuote}
                  onChange={(e) => setRvQuote(e.target.value)}
                  placeholder="Paste or type the feedback they gave you"
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="admin-review-add-button"
                disabled={addingReview}
                className="flex w-full items-center justify-center gap-2 bg-gold px-6 py-3 font-cond font-bold uppercase tracking-[0.18em] text-black transition-all hover:bg-gold-hover active:scale-95 disabled:opacity-60"
              >
                <Plus size={15} />
                {addingReview ? "Publishing…" : "Publish review"}
              </button>
            </form>

            <div className="space-y-4 lg:col-span-7">
              {reviews.length === 0 ? (
                <p data-testid="admin-reviews-empty" className="border border-line bg-panel px-6 py-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">
                  No reviews yet — add the first one
                </p>
              ) : (
                reviews.map((r) => (
                  <article
                    key={r.id}
                    data-testid={`admin-review-card-${r.id}`}
                    className={`border bg-panel p-5 ${r.approved ? "border-line" : "border-line/50 opacity-60"}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-cond text-lg font-bold uppercase tracking-[0.06em] text-zinc-50">
                          {r.customer_name}
                          {r.vehicle && (
                            <span className="ml-3 font-mono text-[11px] font-normal uppercase tracking-[0.18em] text-zinc-400">
                              {r.vehicle}
                            </span>
                          )}
                        </p>
                        <Stars rating={r.rating} className="mt-1" />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleReview(r)}
                          data-testid={`admin-review-approve-toggle-${r.id}`}
                          title={r.approved ? "Hide from site" : "Show on site"}
                          className={`flex items-center gap-2 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${r.approved ? "border-emerald-400/40 text-emerald-400 hover:border-emerald-400" : "border-line text-zinc-400 hover:border-gold hover:text-gold"}`}
                        >
                          {r.approved ? <Eye size={12} /> : <EyeOff size={12} />}
                          {r.approved ? "Live" : "Hidden"}
                        </button>
                        <button
                          onClick={() => deleteReview(r)}
                          data-testid={`admin-review-delete-${r.id}`}
                          title="Delete review"
                          className="border border-line p-1.5 text-zinc-400 transition-colors hover:border-red-400 hover:text-red-400"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-300">“{r.quote}”</p>
                  </article>
                ))
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
